import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getHiddenApps, hideApp, unhideApp } from '../services/hiddenAppsService';

export function useHiddenApps() {
  const { user } = useAuth();
  const [hiddenAppIds, setHiddenAppIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHidden = useCallback(async () => {
    if (!user?.email) {
      setHiddenAppIds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const ids = await getHiddenApps(user.email);
      setHiddenAppIds(ids);
    } catch (err) {
      console.error('[useHiddenApps] Failed:', err);
      setHiddenAppIds([]);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => { fetchHidden(); }, [fetchHidden]);

  const hide = useCallback(async (appId: string) => {
    if (!user?.email) return;
    setHiddenAppIds((prev) => [...prev, appId]);
    try {
      await hideApp(user.email, appId);
    } catch {
      setHiddenAppIds((prev) => prev.filter((id) => id !== appId));
    }
  }, [user?.email]);

  const show = useCallback(async (appId: string) => {
    if (!user?.email) return;
    setHiddenAppIds((prev) => prev.filter((id) => id !== appId));
    try {
      await unhideApp(user.email, appId);
    } catch {
      setHiddenAppIds((prev) => [...prev, appId]);
    }
  }, [user?.email]);

  const isHidden = useCallback(
    (appId: string) => hiddenAppIds.includes(appId),
    [hiddenAppIds]
  );

  return { hiddenAppIds, loading, hide, show, isHidden, refresh: fetchHidden };
}
